// https://github.com/Updater/frontend-core/tree/main/packages/ui-tracker for docs
import {
  Tracker,
  eventType,
  EventTypeMap,
  TrackEventInput,
} from '@updater/ui-tracker';
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useTracking } from 'react-tracking';
import { useServerProps } from './ServerProps';
import { useDeviceId } from './use-device-id';

const rootEventTypeMap: EventTypeMap = {
  movingApp: {
    app_events: {
      loaded: eventType(),
    },
  },
};

const RootTrackerContext = React.createContext<
  Tracker<typeof rootEventTypeMap>
>(undefined!);

export const useRootTracker = () => useContext(RootTrackerContext);

interface RootTrackerProviderProps extends PropsWithChildren {
  appName?: string;
}

export const RootTrackerProvider: React.FC<RootTrackerProviderProps> = ({
  children,
  appName = 'consumer-app',
}) => {
  const { env, eventStream } = useServerProps();
  const rootTracker = useMemo(
    () =>
      new Tracker({
        eventTypeMap: rootEventTypeMap,
        environment: env,
        appName,
        destinations: {
          eventstream: {
            app: appName,
            url: eventStream.url,
            batchUrl: eventStream.batchUrl,
            authorization: eventStream.token,
          },
        },
        channel: 'consumer',
        debug: true,
      }),
    [env, eventStream, appName]
  );

  // clean up the tracker's set interval
  useEffect(() => rootTracker.destroy, [rootTracker]);

  const deviceId = useDeviceId();

  const { Track } = useTracking<TrackEventInput<unknown>>(
    {
      appName,
      details: {
        // eslint-disable-next-line no-underscore-dangle
        app_version: global.__VERSION_STRING__,
        app_name: appName,
        // camel for existing events 🐫
        deviceId,
      },
    },
    {
      dispatch: (event) => rootTracker.trackUntyped(event),
    }
  );
  return (
    <Track>
      <RootTrackerContext.Provider value={rootTracker}>
        {children}
      </RootTrackerContext.Provider>
    </Track>
  );
};
